/**
 * Turning the chart's bars into the shape the Elliott endpoint takes.
 *
 * <h2>The ordering trap</h2>
 *
 * The chart stores each bar as `[open, close, low, high]` — ECharts' candlestick order, which is
 * NOT the open-high-low-close order the name OHLC implies. Everything else in this app reads those
 * positions individually (`bar[1]` for close, `bar[3]` for high), so the mismatch never surfaces.
 *
 * Sending the array straight through would be the obvious thing to do and would silently transpose
 * high and close for every bar. That does not fail: the engine would label a series that never
 * traded, return a confident count, and nobody would have any reason to doubt it. Hence named
 * fields on the wire, and hence this being its own tested unit rather than three lines inside a
 * click handler.
 */

/** Positions within the chart's own bar array. Named so the mapping below reads as intended. */
const OPEN = 0;
const CLOSE = 1;
const LOW = 2;
const HIGH = 3;

/** Epoch millis for a chart date, or null when it cannot be read as one. */
function toTimestamp(date) {
  if (!date) return null;
  const parsed = Date.parse(typeof date === 'string' ? date.slice(0, 10) : date);
  return Number.isNaN(parsed) ? null : parsed;
}

function finite(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * The bars to send, in chart order.
 *
 * Bars whose prices are missing or non-numeric are dropped here as well as server-side. The chart's
 * series carries a projection window at the end — dates with no prices, which the studies draw
 * into — and those are not bars the market traded.
 *
 * <h2>Anchoring</h2>
 *
 * `upToBarIndex` cuts the series at a chart bar, INCLUSIVE. The engine returns only current
 * patterns, so what "current" means is decided entirely by where the series ends — cutting the
 * input is therefore the whole of anchoring the count to a chosen bar.
 *
 * It has to be a cut rather than a filter of the answer. The engine ranks and prunes counts against
 * the series it is given; hand it the full history and discard the counts that reach past the
 * anchor, and the survivors were still chosen in competition with counts built from bars the
 * reader had not seen yet. Truncating removes the future rather than hiding it, which is the only
 * version of this that can be checked afterwards against what price actually did.
 *
 * The cut is applied to the CHART index, before unusable bars are dropped, so the result stays a
 * prefix of the untruncated list. That is what keeps the engine's bar indices lining up with the
 * chart's — the existing feature already relies on it, since the projection window at the end of
 * the series is dropped the same way.
 *
 * @param {{dates: Array<string>, ohlc: Array<Array<number>>, volumes?: Array<number>}} barData
 * @param {number} [upToBarIndex] last chart bar to include; omitted means the whole series
 * @returns {Array<{timestamp: number|null, open: number, high: number, low: number, close: number, volume: number}>}
 */
export function toElliottBars(barData, upToBarIndex) {
  if (!barData || !Array.isArray(barData.dates) || !Array.isArray(barData.ohlc)) return [];

  const out = [];
  let count = Math.min(barData.dates.length, barData.ohlc.length);
  if (Number.isInteger(upToBarIndex)) {
    // A negative anchor yields no bars, and the caller is told so by the bar-count check rather
    // than by silently getting the whole series — which is what Math.max(0, ...) would do.
    count = Math.min(count, upToBarIndex + 1);
  }

  for (let i = 0; i < count; i++) {
    const bar = barData.ohlc[i];
    if (!Array.isArray(bar) || bar.length < 4) continue;

    const open = bar[OPEN];
    const close = bar[CLOSE];
    const low = bar[LOW];
    const high = bar[HIGH];
    if (!finite(open) || !finite(close) || !finite(low) || !finite(high)) continue;

    const volume = Array.isArray(barData.volumes) ? barData.volumes[i] : 0;
    out.push({
      timestamp: toTimestamp(barData.dates[i]),
      open,
      high,
      low,
      close,
      volume: finite(volume) ? Math.round(volume) : 0,
    });
  }
  return out;
}

/**
 * The full request body for POST /api/charts/elliott-analysis.
 *
 * @param {object} barData the chart's bars
 * @param {object} [opts]
 * @param {string} [opts.symbol]
 * @param {string} [opts.timeframe]
 * @param {number} [opts.upToBarIndex] treat this chart bar as the last one — see toElliottBars
 */
export function buildElliottRequest(barData, { symbol, timeframe = 'Daily', upToBarIndex } = {}) {
  return {
    symbol: symbol || barData?.symbol || 'UNKNOWN',
    timeframe,
    bars: toElliottBars(barData, upToBarIndex),
  };
}

/** Bars below this cannot produce a count, so the round trip is not worth making. */
export const MIN_BARS_FOR_ANALYSIS = 20;

/**
 * High/low per chart bar, aligned 1:1 with the chart's own indices.
 *
 * <p>Deliberately NOT `toElliottBars`. That one drops unusable bars, which is right for the engine
 * — it wants a series — but wrong for verification, which asks "what did price do at bar N". A
 * dropped bar there would shift every later index by one and silently check the wrong bars.
 * Unusable bars come back as `null` and are skipped by the caller, keeping position meaningful.
 *
 * @param {{ohlc: Array<Array<number>>}} barData
 * @returns {Array<{high: number, low: number, close: number}|null>}
 */
export function toVerificationBars(barData) {
  if (!barData || !Array.isArray(barData.ohlc)) return [];
  return barData.ohlc.map((bar) => {
    if (!Array.isArray(bar) || bar.length < 4) return null;
    const close = bar[CLOSE];
    const low = bar[LOW];
    const high = bar[HIGH];
    if (!finite(close) || !finite(low) || !finite(high)) return null;
    return { high, low, close };
  });
}
